import { useEffect, useRef } from 'react'
import { color, type State } from './tokens'
import { gsap, EASE_OUT, prefersReducedMotion } from './gsap'
import { LOOP_BOX, pointOnStadium, stadiumMetrics, stadiumPath } from './loop/stadium'

export type LoopNodeState = State | 'idle'
export interface LoopNode { t: number; state: LoopNodeState; radius?: number }
export interface LoopStation { id: string; label: string; t: number; state: LoopNodeState }

const STATE_COLOR: Record<LoopNodeState, string> = {
  idle: color.neutro800, construcao: color.construcao, conexao: color.conexao, gate: color.gate, cura: color.cura, falha: color.falha,
}
const PULSE_TRAIL = [5.5, 4.5, 3.5, 2.5] // raios do cometa — a cauda encolhe, nunca esmaece (cor de estado não tem alfa)

/**
 * Diagrama do laço (portado do modelo do Ângelo, 2026-09-06): um único caminho-estádio, nós por fração de arco,
 * cores pela lei semântica. `pulse` = um cometa percorre o laço sem nunca sair dele ("nada acontece fora do laço").
 * `animateIn` = desenha o traço e acende os nós uma vez, via GSAP (easing do arco), sem keyframes em CSS.
 * `stations` = nós grandes clicáveis (explorador); os rótulos ficam fora do SVG, como HTML real.
 * Reduced-motion: tudo estático no primeiro frame. Halo do nó = anel de traço, não alfa (ADR-006).
 */
export function LoopDiagram({ nodes = [], stations, activeStationId, onStationSelect, animateIn = false, pulse = false, pulseColor = color.construcaoOnInk, pulseDuration = 5.5, className, title }: {
  nodes?: LoopNode[]; stations?: LoopStation[]; activeStationId?: string; onStationSelect?: (id: string) => void
  animateIn?: boolean; pulse?: boolean; pulseColor?: string; pulseDuration?: number; className?: string; title?: string
}) {
  const ref = useRef<SVGSVGElement>(null)
  const { viewW, viewH, pad } = LOOP_BOX
  const w = viewW - pad * 2, h = viewH - pad * 2, r = h / 2
  const { perimeter } = stadiumMetrics(w, r)
  const d = stadiumPath(w, h, r)
  const reduced = prefersReducedMotion()
  const showPulse = pulse && !reduced

  useEffect(() => {
    const svg = ref.current
    if (!svg || !animateIn || prefersReducedMotion()) return
    const path = svg.querySelector<SVGPathElement>('[data-loop-path]')
    const dots = svg.querySelectorAll<SVGGElement>('[data-loop-node]')
    if (!path) return
    const ctx = gsap.context(() => {
      gsap.set(path, { strokeDasharray: perimeter, strokeDashoffset: perimeter })
      gsap.set(dots, { opacity: 0, scale: 0.4, transformOrigin: '50% 50%' })
      const tl = gsap.timeline()
      tl.to(path, { strokeDashoffset: 0, duration: 1.1, ease: EASE_OUT })
      tl.to(dots, { opacity: 1, scale: 1, duration: 0.4, ease: EASE_OUT, stagger: 0.06 }, '-=0.3')
      tl.set(path, { strokeDasharray: 'none' }) // traço fechado e inteiro ao final: nenhum caminho termina no vazio
    }, svg)
    return () => ctx.revert()
  }, [animateIn, perimeter])

  return (
    // com estações clicáveis o SVG é um grupo de controles (role=group), nunca uma imagem com filhos focáveis (aria-hidden-focus)
    <svg ref={ref} viewBox={`0 0 ${viewW} ${viewH}`} className={['z-loop', className].filter(Boolean).join(' ')} role={stations ? 'group' : 'img'} aria-label={title} aria-hidden={title || stations ? undefined : true} data-anatomy="grafismo">
      <g transform={`translate(${pad},${pad})`}>
        <path data-loop-path d={d} fill="none" className="z-loop__traco" strokeWidth={2} />
        {showPulse && PULSE_TRAIL.map((radius, i) => (
          <circle key={i} r={radius} fill={pulseColor}>
            <animateMotion dur={`${pulseDuration}s`} begin={animateIn ? `${1.4 + i * 0.12}s` : `-${i * 0.12}s`} repeatCount="indefinite" path={d} keyPoints="1;0" keyTimes="0;1" calcMode="linear" rotate="auto" />
          </circle>
        ))}
        {nodes.map((node, i) => {
          const { x, y } = pointOnStadium(node.t, w, h, r)
          const radius = node.radius ?? (node.state === 'idle' ? 6 : 9)
          const c = STATE_COLOR[node.state]
          return (
            <g key={i} data-loop-node data-role="no">
              {node.state !== 'idle' && <circle cx={x} cy={y} r={radius + 5} fill="none" stroke={c} strokeWidth={1.5} />}
              <circle cx={x} cy={y} r={radius} fill={c} />
            </g>
          )
        })}
        {stations?.map((s) => {
          const { x, y } = pointOnStadium(s.t, w, h, r)
          const c = STATE_COLOR[s.state]
          const active = s.id === activeStationId
          const interactive = Boolean(onStationSelect)
          return (
            <g key={s.id} data-role="no" role={interactive ? 'button' : undefined} tabIndex={interactive ? 0 : undefined} aria-pressed={interactive ? active : undefined} aria-label={interactive ? s.label : undefined}
              className={interactive ? 'z-loop__estacao' : undefined} onClick={interactive ? () => onStationSelect?.(s.id) : undefined}
              onKeyDown={interactive ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onStationSelect?.(s.id) } } : undefined}>
              <circle cx={x} cy={y} r={22} fill="transparent" />
              <circle cx={x} cy={y} r={active ? 15 : 11} fill="none" stroke={c} strokeWidth={active ? 2 : 1.5} className="z-loop__anel" />
              <circle cx={x} cy={y} r={active ? 10 : 7.5} fill={c} stroke={active ? color.papel : 'none'} strokeWidth={active ? 2 : 0} className="z-loop__no" />
            </g>
          )
        })}
      </g>
    </svg>
  )
}
